// pages/affiliates/AffiliateDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import styles from './AffiliateDashboard.module.css';
import ContractModal from './ContractModal';

// Config plans pour affichage
const PLANS_CONFIG = {
    STARTER: { price: '5.99$ / 5.49€', commission: '3€ / $3', color: '#3b82f6' },
    PREMIUM: { price: '11.99$ / 10.99€', commission: '5.50€ / $5.5', color: '#ec4899' },
    PRO:     { price: '23.99$ / 21.99€', commission: '12€ / $12', color: '#eab308' }
};

const formatPrice = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00€';
    return (num / 100).toFixed(2) + '€';
};
 

const AffiliateDashboard = () => {
    const navigate = useNavigate();
    const [affiliate, setAffiliate] = useState(null);
    const [stats, setStats] = useState(null);
    const [conversions, setConversions] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showPayoutsModal, setShowPayoutsModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [contractAccepting, setContractAccepting] = useState(false);
    const [contractError, setContractError] = useState('');
    const [contractAcceptedLocally, setContractAcceptedLocally] = useState(false);
    const token = localStorage.getItem('affiliateToken');

    // Vérifier auth au chargement
    useEffect(() => {
        if (!token) {
            navigate('/affiliates/login');
            return;
        }
        loadData();
    }, [token, navigate]);

    const loadData = async () => {
        try {
            setLoading(true);
            console.log('📊 Chargement données affilié...');

            // ✅ Configuration avec token affilié
            const config = {
                headers: { 'Authorization': `Bearer ${token}` }
            };

            // Charger les infos affilié
            const meResponse = await api.get('/affiliates/me', config);
            if (!meResponse.data.success) {
                throw new Error(meResponse.data.message);
            }
            setAffiliate(meResponse.data.affiliate);
            if (!meResponse.data.affiliate.contract_accepted_at && !contractAcceptedLocally) {
                setShowContractModal(true);
            } else {
                setShowContractModal(false);
            }
            console.log('✅ Infos affilié chargées');

            // Charger les stats
            const statsResponse = await api.get('/affiliates/stats', config);
            if (statsResponse.data.success) {
                setStats(statsResponse.data);
                console.log('✅ Stats chargées');
            }

            // Charger les conversions
            const conversionsResponse = await api.get('/affiliates/conversions?limit=10', config);
            if (conversionsResponse.data.success) {
                setConversions(conversionsResponse.data.conversions);
                console.log('✅ Conversions chargées');
            }

            // Charger les payouts
            const payoutsResponse = await api.get('/affiliates/payouts', config);
            if (payoutsResponse.data.success) {
                setPayouts(payoutsResponse.data.payouts);
                console.log('✅ Payouts chargés');
            }

        } catch (err) {
            console.error('❌ Erreur chargement données:', err);
            if (err.response?.status === 401 || err.message?.includes('Token')) {
                localStorage.removeItem('affiliateToken');
                localStorage.removeItem('affiliateInfo');
                navigate('/affiliates/login');
            } else {
                setError('Erreur de chargement des données');
            }
        } finally {
            setLoading(false);
        }
    };

useEffect(() => {
    if (!token) {
        navigate('/affiliates/login');
        return;
    }
    loadData();
}, []); // ← tableau vide, exécution unique au montage

 const handleAcceptContract = async () => {
    setContractAccepting(true);
    setContractError('');

    try {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        const response = await api.post('/affiliates/accept-contract', {}, config);

        if (response.data.success) {
            const acceptedAt = new Date().toISOString();

            setContractAcceptedLocally(true);
            setShowContractModal(false);

            setAffiliate(prev =>
                prev
                    ? { ...prev, contract_accepted_at: acceptedAt }
                    : prev
            );

            await loadData();
        } else {
            setContractError(response.data.message || "Erreur lors de l'acceptation");
        }
    } catch (err) {
        console.error('Erreur acceptation contrat:', err.response?.data || err.message);
        setContractError(
            err.response?.data?.message || 'Erreur réseau — réessaie'
        );
    } finally {
        setContractAccepting(false);
    }
};

    const handleLogout = () => {
        localStorage.removeItem('affiliateToken');
        localStorage.removeItem('affiliateInfo');
        navigate('/affiliates/login');
    };

    const copyLink = () => {
        if (affiliate?.link) {
            navigator.clipboard.writeText(affiliate.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className={`${styles.dashboard} ${styles.loading}`}>
                <div className={styles.loader}></div>
                <p>Chargement...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${styles.dashboard} ${styles.errorState}`}>
                <p>{error}</p>
                <button onClick={loadData} className={styles.retryBtn}>Réessayer</button>
            </div>
        );
    }

    return (
        <div className={styles.dashboard}>
            <div className={styles.container}>
                {/* Header */}
                <div className={styles.header}>
                    <div>
                        <h1>🚀 Espace Affilié</h1>
                        <p>Bienvenue, {affiliate?.name}</p>
                    </div>
                    <button onClick={handleLogout} className={styles.logoutBtn}>
                        Déconnexion
                    </button>
                </div>

                {/* Lien affilié */}
                <div className={styles.linkCard}>
                    <p className={styles.label}>Ton lien affilié</p>
                    <div className={styles.linkRow}>
                        <div className={styles.linkDisplay}>
                            {affiliate?.link}
                        </div>
                        <button onClick={copyLink} className={styles.copyBtn}>
                            {copied ? '✓ Copié !' : '📋 Copier'}
                        </button>
                    </div>
                    <p className={styles.codeInfo}>
                        Code promo : <span className={styles.code}>{affiliate?.code}</span>
                    </p>
                </div>

                {/* Stats principales */}
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <p className={styles.statLabel}>Clics</p>
                        <p className={styles.statValue}>{stats?.stats?.clicks || 0}</p>
                    </div>
                    <div className={styles.statCard}>
                        <p className={styles.statLabel}>Inscrits</p>
                        <p className={`${styles.statValue} ${styles.blue}`}>{stats?.stats?.signups || 0}</p>
                    </div>
                    <div className={styles.statCard}>
                        <p className={styles.statLabel}>Abonnés payants</p>
                        <p className={`${styles.statValue} ${styles.green}`}>{stats?.stats?.paidUsers || 0}</p>
                    </div>
                    <div className={styles.statCard}>
                        <p className={styles.statLabel}>Taux conversion</p>
                        <p className={`${styles.statValue} ${styles.yellow}`}>{stats?.stats?.conversionRate || 0}%</p>
                    </div>
                </div>

                {/* Répartition par plan */}
                <div className={styles.plansCard}>
                    <div className={styles.plansHeader}>
                        <h2>📦 Répartition par plan (ce mois)</h2>
                        <button onClick={() => setShowInfoModal(true)} className={styles.infoBtn}>
                            ℹ️ Comment c'est calculé ?
                        </button>
                    </div>
                    <div className={styles.plansGrid}>
                        {Object.entries(PLANS_CONFIG).map(([planName, config]) => {
                            const planStats = stats?.stats?.byPlan?.[planName] || { count: 0, commission: 0 };
                            return (
                                <div 
                                    key={planName} 
                                    className={`${styles.planCard} ${styles[planName.toLowerCase()]}`}
                                >
                                    <div className={styles.planHeader}>
                                        <span className={styles.planName}>{planName}</span>
                                       <span className={styles.planPrice}>{config.price}/mois</span>
                                    </div>
                                    <p className={styles.planCount}>{planStats.count}</p>
                                    <p className={styles.planCommission}>
                                        Gains : <span className={styles.green}>{formatPrice(planStats.commission)}</span>
                                    </p>
                                    <p className={styles.planCalc}>
                                        ({config.commission} × {planStats.count})
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Gains */}
                <div className={styles.earningsGrid}>
                    <div className={`${styles.earningsCard} ${styles.pending}`}>
                        <p className={styles.earningsLabel}>💰 Gains en attente (ce mois)</p>
                        <p className={styles.earningsValue}>{formatPrice(stats?.earnings?.pending || 0)}</p>
                        <div className={styles.earningsInfo}>
                            <p>Période : {stats?.period?.start} → {stats?.period?.end}</p>
                            <p className={styles.highlight}>⏰ Paiement dans {stats?.period?.daysLeft} jours</p>
                        </div>
                    </div>
                    <div className={`${styles.earningsCard} ${styles.paid}`}>
                        <p className={styles.earningsLabel}>✅ Total déjà payé</p>
                        <p className={`${styles.earningsValue} ${styles.green}`}>{formatPrice(stats?.earnings?.paid || 0)}</p>
                        <button onClick={() => setShowPayoutsModal(true)} className={styles.linkBtn}>
                            Voir mes factures →
                        </button>
                    </div>
                </div>

                {/* Dernières conversions */}
                <div className={styles.conversionsCard}>
                    <h2>📊 Dernières conversions</h2>
                    {conversions.length === 0 ? (
                        <p className={styles.noData}>Aucune conversion pour le moment</p>
                    ) : (
                        <div className={styles.conversionsTable}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Plan</th>
                                        <th>Statut</th>
                                        <th>Commission</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {conversions.map((c) => (
                                        <tr key={c.id}>
                                            <td>{formatDate(c.date)}</td>
                                            <td>
                                                <span className={`${styles.planBadge} ${styles[c.planName?.toLowerCase()]}`}>
                                                    {c.planName}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${styles[c.status]}`}>
                                                    {c.status === 'confirmed' ? 'Confirmé ✓' : 'En attente'}
                                                </span>
                                            </td>
                                            <td className={styles.commission}>+{formatPrice(c.commissionAmount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={styles.dashboardFooter}>
                    Un souci ? Contacte-nous → rizzingbot@gmail.com
                </div>
            </div>

            {/* Modal Info */}
            {showInfoModal && (
                <div className={styles.modalOverlay} onClick={() => setShowInfoModal(false)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>💡 Comment sont calculés tes gains ?</h3>
                            <button onClick={() => setShowInfoModal(false)}>×</button>
                        </div>
                        <div className={styles.modalBody}>
                         <p>
                            Tu reçois une <span className={styles.pink}>commission fixe</span> sur le 
                            premier mois d'abonnement pour chaque utilisateur via ton lien.
                            À partir du mois 2, l'abonnement me revient entièrement.
                        </p>

                        <div className={styles.commissionList}>
                            <p className={styles.title}>Commissions par plan :</p>
                            <div className={styles.commissionItem}>
                                <span className={styles.blue}>STARTER (5.99$ / 5.49€)</span>
                                <span className={styles.green}>→ 3€ / $3 pour toi</span>
                            </div>
                            <div className={styles.commissionItem}>
                                <span className={styles.pink}>PREMIUM (11.99$ / 10.99€)</span>
                                <span className={styles.green}>→ 5.50€ / $5.5 pour toi</span>
                            </div>
                            <div className={styles.commissionItem}>
                                <span className={styles.yellow}>PRO (23.99$ / 21.99€)</span>
                                <span className={styles.green}>→ 12€ / $12 pour toi</span>
                            </div>
                        </div>

                        <p className={styles.small}>
                            Commission versée uniquement sur le premier mois. 
                            Les frais Stripe, hébergement et API sont couverts par la marge restante.
                        </p>

                            <p>
                                💸 <strong>Paiement</strong> : Virement bancaire chaque fin de mois.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Payouts */}
            {showPayoutsModal && (
                <div className={styles.modalOverlay} onClick={() => setShowPayoutsModal(false)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>📄 Mes factures</h3>
                            <button onClick={() => setShowPayoutsModal(false)}>×</button>
                        </div>
                        <div className={styles.modalBody}>
                            {payouts.length === 0 ? (
                                <p className={styles.noData}>Aucun paiement pour le moment</p>
                            ) : (
                                <div className={styles.payoutsList}>
                                    {payouts.map(p => (
                                        <div key={p.id} className={styles.payoutItem}>
                                            <div>
                                                <p className={styles.payoutPeriod}>
                                                    {formatDate(p.periodStart)} - {formatDate(p.periodEnd)}
                                                </p>
                                                <p className={styles.payoutAmount}>
                                                    {formatPrice(p.netAmount)} • {p.status === 'paid' ? 'Payé' : p.status}
                                                </p>
                                            </div>
                                            {p.invoiceNumber && (
                                                <button className={styles.pdfBtn}>PDF ↓</button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className={`${styles.small} ${styles.center}`}>
                                Les factures sont générées après chaque paiement
                            </p>
                        </div>
                    </div>
                </div>
            )}

              {/* Modal Contrat */}
        <ContractModal
            show={showContractModal}
            onAccept={handleAcceptContract}
            accepting={contractAccepting}
            error={contractError}
        />
        </div>

      
    );
};

export default AffiliateDashboard;